import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const from = location.state?.from?.pathname || '/';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);
    setLoading(false);

    if (result.success) {
      toast.success('Welcome back!');
      navigate(from, { replace: true });
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex">
      {/* Left Side - Form */}
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md"
        >
          <div className="text-center mb-8">
            <Link to="/" className="inline-block mb-8">
              <h1 className="font-black text-3xl tracking-tighter" style={{ fontFamily: "'Outfit', sans-serif" }}>
                DOE WIZ
              </h1>
            </Link>
            <h2 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
              WELCOME BACK
            </h2>
            <p className="text-[#525252] mt-2">Sign in to continue shopping</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-[#FAD4D4] border-2 border-[#0A0A0A] p-4 text-sm" data-testid="login-error">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-semibold mb-2">
                Email Address
              </label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                data-testid="login-email"
                className="input-brutalist w-full rounded-none"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-semibold mb-2">
                Password
              </label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  data-testid="login-password"
                  className="input-brutalist w-full rounded-none pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full bg-[#0A0A0A] text-white border-2 border-[#0A0A0A] rounded-none h-14 font-semibold text-base hover:translate-y-[-2px] hover:shadow-[4px_4px_0px_rgba(0,0,0,0.3)] transition-all disabled:opacity-50"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <p className="text-center mt-8">
            Don't have an account?{' '}
            <Link to="/register" className="font-semibold underline" data-testid="register-link">
              Create one
            </Link>
          </p>
        </motion.div>
      </div>

      {/* Right Side - Image (Desktop Only) */}
      <div className="hidden lg:block lg:w-1/2 border-l-2 border-[#0A0A0A]">
        <div className="h-full bg-[#FAD4D4] flex items-center justify-center p-12">
          <div className="max-w-lg text-center">
            <h3 className="text-4xl font-black tracking-tight mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              STYLE FOR THE WHOLE FAMILY
            </h3>
            <p className="text-[#525252]">
              Join the Doe Wiz community and discover your perfect look.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
